import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpVisibilityInput, type HelpVisibility } from '@nucleus-commerce/api-client/admin';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface VisibilityRadioGroupProps extends Omit<
  ComponentProps<typeof RadioGroup>,
  'value' | 'onValueChange' | 'onChange'
> {
  /** Prefix for the item ids, unique per form. */
  idPrefix: string;
  value: HelpVisibility;
  onChange: (value: HelpVisibility) => void;
  /** Hint keys under `helpCenter:` per visibility, so articles and categories explain themselves. */
  hintKey: 'visibilityHint' | 'categoryVisibilityHint';
}

/** Public or internal, each with a one-line hint; shared by the article editor and the dialog. */
export function VisibilityRadioGroup({
  idPrefix,
  value,
  onChange,
  hintKey,
  className,
  ...rest
}: VisibilityRadioGroupProps) {
  const { t } = useTranslation('helpCenter');
  return (
    <RadioGroup
      {...rest}
      value={value}
      onValueChange={(next) => onChange(next as HelpVisibility)}
      className={cn('gap-2.5', className)}
    >
      {Object.values(HelpVisibilityInput).map((visibility) => (
        <div key={visibility} className="flex items-start gap-2.5">
          <RadioGroupItem value={visibility} id={`${idPrefix}-${visibility}`} className="mt-0.5" />
          <div className="grid gap-0.5">
            <Label htmlFor={`${idPrefix}-${visibility}`}>{t(`visibility.${visibility}`)}</Label>
            <span className="text-muted-foreground text-xs">{t(`${hintKey}.${visibility}`)}</span>
          </div>
        </div>
      ))}
    </RadioGroup>
  );
}
